import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Instagram } from "lucide-react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import { spath } from "./LanguageSwitcher";
import { LEGAL_LABEL } from "@/lib/legalContent";

const INSTAGRAM_URL = "https://www.instagram.com/sifirbirtuning";
const PHONE = "+90 542 878 64 99";
const COMPANY_LABEL = { tr: "Kurumsal", en: "Company", el: "Εταιρεία", ru: "Компания", fa: "شرکت" };

export default function Footer() {
  const { lang } = useParams();
  const { data: nav } = useQuery({
    queryKey: ["nav", lang],
    queryFn: async () => (await api.get(`/public/nav/${lang}`)).data,
  });
  const services = nav?.services || [];
  const branches = nav?.branches || [];
  const year = new Date().getFullYear();
  const linkCls = "text-sm text-subtle hover:text-copper transition-colors";
  const headCls = "text-xs uppercase tracking-widest text-faint mb-4";

  return (
    <footer className="px-4 sm:px-6 lg:px-8 pb-8 pt-16 bg-obsidian">
      <div className="max-w-7xl mx-auto rounded-3xl border border-white/10 bg-graphite/60 p-8 md:p-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-10">
          {/* Brand */}
          <div className="col-span-2">
            <img src="/logo.jpg" alt="Sıfır Bir Tuning" className="h-16 w-auto" />
            <p className="text-subtle text-sm mt-4 max-w-xs leading-relaxed">
              {lang === "tr"
                ? "KKTC'nin premium araç modifiye ve kaplama stüdyosu."
                : "Premium automotive customization studio in North Cyprus."}
            </p>
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram"
              aria-label="Instagram"
              className="inline-flex items-center gap-2 mt-5 px-4 py-2 rounded-full border border-white/15 text-sm text-ink hover:border-copper hover:text-copper transition-colors">
              <Instagram size={17} /> @sifirbirtuning
            </a>
          </div>

          {/* Services */}
          <div>
            <p className={headCls}>{t(lang, "services")}</p>
            <ul className="space-y-2.5">
              {services.map((s) => (
                <li key={s.entity_id}>
                  <Link to={spath(lang, s.slug)} className={linkCls}>{(s.title || "").replace(/^KKTC\s+/i, "")}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <p className={headCls}>{COMPANY_LABEL[lang] || COMPANY_LABEL.en}</p>
            <ul className="space-y-2.5">
              {nav?.blog?.available && (
                <li><Link to={`/${lang}/blog`} className={linkCls}>{t(lang, "blog")}</Link></li>
              )}
              <li><a href="#faq" className={linkCls}>{faqLabel(lang)}</a></li>
              <li><a href="#quote" className={linkCls}>{t(lang, "contact")}</a></li>
              {branches.map((b) => (
                <li key={b.entity_id}><Link to={spath(lang, b.slug)} className={linkCls}>{b.title}</Link></li>
              ))}
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-white/10 mt-10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-faint">
          <span>© {year} Sıfır Bir Tuning. {t(lang, "rights")}</span>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <Link to={spath(lang, "privacy")} data-testid="footer-privacy-link" className="hover:text-copper transition-colors">{LEGAL_LABEL.privacy[lang] || LEGAL_LABEL.privacy.en}</Link>
            <Link to={spath(lang, "terms")} data-testid="footer-terms-link" className="hover:text-copper transition-colors">{LEGAL_LABEL.terms[lang] || LEGAL_LABEL.terms.en}</Link>
            <a href={`tel:${PHONE.replace(/\s/g, "")}`} className="hover:text-copper transition-colors">{PHONE}</a>
            <span className="hidden sm:inline">Girne · Lefkoşa · Mağusa</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

const FAQ_LABEL = { tr: "SSS", en: "FAQ", el: "Συχνές Ερωτήσεις", ru: "Вопросы", fa: "سوالات" };
function faqLabel(lang) { return FAQ_LABEL[lang] || FAQ_LABEL.en; }
